import { ChangeContext, Options } from '@angular-slider/ngx-slider';
import { Component, DoCheck, Input } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import 'hammerjs';
import * as _ from 'lodash';
import { IControlInput } from '../../shared/models/device';
import { GraphicBehaviorType } from '../../shared/models/graphic-behavior-type.enum';

@Component({
    selector: 'dl-slider-number-sensor',
    templateUrl: './slider-number-sensor-input.component.html',
    styleUrls: ['./slider-number-sensor-input.component.scss']
})
export class SliderNumberSensorInputComponent implements DoCheck {
    public static $name: string = 'sliderNumberSensor';

    @Input() units: string;
    @Input() step: string;
    @Input() deviceInput: IControlInput;
    @Input() inputFormControl: FormControl;
    @Input() getValidators: () => ValidatorFn[];
    @Input() invokeCommand: () => void;
    @Input() deviceInputForm: FormGroup;

    editStep: boolean = false;
    flooredValue: number;
    isTemperature: boolean = false;
    isHeat: boolean = false;
    isCool: boolean = false;
    decimalValue: number;

    options: Options = {
        showSelectionBar: true,
        enforceStepsArray: false
    };

    stepOptions: Options = {
        showSelectionBar: true,
        showTicks: true
    };

    public readonly debounceTime = 300;

    textboxChange = _.debounce((event: any) => {
        this.flooredValue = event.target.value;
        this.decimalValue = event.target.value;
        this.updateStepRange();
        this.updateSliderRange();
        this.invokeCommand();
    }, this.debounceTime, {});

    private canEditStep: boolean = false;
    private oldMin: number;
    private oldMax: number;

    public ngOnInit() {
        this.flooredValue = this.deviceInput.value;
        this.decimalValue = this.deviceInput.value;
        this.oldMin = this.deviceInput.minValue;
        this.oldMax = this.deviceInput.maxValue;
        this.options.disabled = !this.deviceInput.isEnabled;
        this.isTemperature = this.deviceInput.elementType === 'TemperatureSensor' || this.deviceInput.elementType === 'TemperatureDifferentialSensor';

        if (this.isTemperature) {
            switch (this.deviceInput.graphicBehavior) {
                case GraphicBehaviorType.TEMPERATURE_HOT_SENSOR:
                case GraphicBehaviorType.TEMPERATURE_HOT_UNOCCUPIED_SENSOR:
                    this.isHeat = true;
                    break;
                case GraphicBehaviorType.TEMPERATURE_COLD_UNOCCUPIED_SENSOR:
                case GraphicBehaviorType.TEMPERATURE_COLD_SENSOR:
                    this.isCool = true;
                    break;
            }
        }

        if (this.units !== undefined && this.units !== '') {
            this.options.translate = (value) => `${value}${this.units}`;
            this.stepOptions.translate = (value) => `${value}${this.units}`;
        }

        if (this.step !== undefined) {
            const numericStep = Number(this.step);
            if(!Number.isNaN(numericStep)) {
                this.canEditStep = numericStep < 1;
                this.stepOptions = {
                    ...this.stepOptions,
                    step: numericStep,
                };
            }
        }

        this.updateSliderRange();
    }

    public ngDoCheck(): void {
        if (this.options.disabled !== !this.deviceInput.isEnabled) {
            this.options = {
                ...this.options,
                disabled: !this.deviceInput.isEnabled
            };
        }

        if (this.oldMin !== this.deviceInput.minValue || this.oldMax !== this.deviceInput.maxValue) {
            this.oldMin = this.deviceInput.minValue;
            this.oldMax = this.deviceInput.maxValue;

            // If there is only one possible value, set the selected value to that value.
            if (this.deviceInput.maxValue !== undefined && this.deviceInput.maxValue === this.deviceInput.minValue) {
                this.deviceInput.value = this.deviceInput.maxValue;
                this.flooredValue = this.deviceInput.maxValue;
                this.inputFormControl.setValue(this.deviceInput.maxValue);
            }

            this.inputFormControl.setValidators(this.getValidators());
            this.inputFormControl.updateValueAndValidity();

            this.updateStepRange();
            this.updateSliderRange();
        }
    }

    public toggleEditStep($event: MatSlideToggleChange) {
        this.editStep = $event.checked;
        this.updateStepRange();
    }

    public attemptSliderChange(changeContext: ChangeContext) {
        this.deviceInputForm.get(this.deviceInput.$fieldName).markAsDirty();
        // Just in case floored value desyncs from when the event is sent, synchronize them.
        this.flooredValue = changeContext.value;
        this.inputFormControl.setValue(this.flooredValue);

        // Disallow editing the decimals if the floored value is the max value
        if (this.flooredValue === this.deviceInput.maxValue) {
            this.canEditStep = false;
        }

        this.updateStepRange();
        this.updateSliderRange();
        this.invokeCommand();
    }

    public attemptSliderDecimalChange(changeContext: ChangeContext) {
        this.deviceInputForm.get(this.deviceInput.$fieldName).markAsDirty();
        this.inputFormControl.setValue(changeContext.value);

        this.updateSliderRange();
        this.invokeCommand();
    }

    public onBlur() {
        if (_.isFunction(this.deviceInput.$onBlur)) {
            this.deviceInput.$onBlur();
        }
    }

    private updateSliderRange() {
        const min = this.deviceInput.minValue;
        const max = this.deviceInput.maxValue;

        if (min > this.flooredValue) {
            this.flooredValue = min;
        } else if (max < this.flooredValue) {
            this.flooredValue = max;
        }

        let steps = min !== max ?
            [min, max].concat(_.range(Math.floor(min + 1), Math.ceil(max - 1) + 1)) :
            // If there is only one possible value, only include that step.
            [max];

        // For some reason, enforceStepsArray doesn't actually allow us to use a non step value, so we need to add the current value as a step.
        if (!steps.includes(this.flooredValue)) {
            steps.push(this.flooredValue);
        }

        this.options = {
            ...this.options,
            // tslint:disable-next-line:no-magic-numbers
            showTicks: steps.length <= 10,
            stepsArray: steps.map(val => {
                return { value: val };
            }).sort((a, b) => a.value - b.value),
        };
    }

    private updateStepRange() {
        if (this.step !== undefined && !Number.isNaN(Number(this.step))) {
            // The decimal slider should go from the selected value to the selected value + 1, but it shouldn't go beyond the min/max.
            const floor = Math.max(this.deviceInput.minValue, Math.floor(this.decimalValue));
            const ceil = Math.min(this.deviceInput.maxValue, Math.ceil(this.decimalValue));

            this.stepOptions = {
                ...this.stepOptions,
                floor: floor,
                // Don't go all the way up to the next whole number.
                ceil: (floor === ceil ? floor + 1 : ceil) - Number(this.step),
            };
        }
    }
}
